import { createElement, type ReactNode, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "@/store/hooks";
import { ROUTES } from "@/shared/constants";
import { showToastDynamic } from "@/shared/utils/toast.util";
import { getNem12BatchStatus } from "../services/nem12ViewerService";
import { isProcessingStatus } from "../utils/nem12Viewer.helpers";

const POLL_INTERVAL_MS = 10_000;

// Module-level (not component state) so the "already shown" flag survives route changes/remounts and only resets for a new batch
const shownBatchStatusRef: { batchId: string | null; status: string | null } = {
  batchId: null,
  status: null,
};

const normalizeStatus = (status: string): string => status.trim().toLowerCase();

const getViewerAction = (onPreview: () => void): ReactNode => {
  return createElement(
    "button",
    {
      type: "button",
      className:
        "text-primary underline hover:underline cursor-pointer bg-transparent border-none p-0",
      onClick: onPreview,
    },
    "NEM12 Viewer",
  );
};

const getStatusMessage = (
  status: string,
  onPreview: () => void,
): { text: ReactNode; type: "success" | "error" } => {
  const normalizedStatus = normalizeStatus(status);
  const viewerAction = getViewerAction(onPreview);

  switch (normalizedStatus) {
    case "complete":
      return {
        text: createElement(
          "span",
          undefined,
          "Your uploaded NEM12 file(s) are processed and ready for the review. Preview the results in the ",
          viewerAction,
          ".",
        ),
        type: "success",
      };
    case "failed":
      return {
        text: createElement(
          "span",
          undefined,
          "Your uploaded NEM12 file(s) failed to process. Please check the ",
          viewerAction,
          " for details.",
        ),
        type: "error",
      };
    case "complete_with_errors":
      return {
        text: createElement(
          "span",
          undefined,
          "Your uploaded NEM12 file(s) are processed with errors. Please check the ",
          viewerAction,
          " for details.",
        ),
        type: "error",
      };
    default:
      return {
        text: createElement(
          "span",
          undefined,
          `NEM12 batch status updated to '${status}'. Please check the `,
          viewerAction,
          " for details.",
        ),
        type: "error",
      };
  }
};

export const useNem12BackgroundPolling = (): void => {
  const responseBatchId = useAppSelector(
    (state) => state.nem12.responseBatchId,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const pollingInFlightRef = useRef<boolean>(false);
  const activeRef = useRef<boolean>(false);
  const timerRef = useRef<number | null>(null);
  const previousStatusRef = useRef<string | null>(null);

  // Skip while the NEM12 Viewer is mounted — it handles its own polling
  const isOnNem12ViewerPage = location.pathname === ROUTES.dataTools.nem12;

  useEffect(() => {
    if (!responseBatchId || isOnNem12ViewerPage) {
      return;
    }

    // Status message already shown for this batch; nothing left to notify about
    if (shownBatchStatusRef.batchId === responseBatchId) {
      return;
    }

    activeRef.current = true;

    const stopPolling = (): void => {
      activeRef.current = false;
      pollingInFlightRef.current = false;
      previousStatusRef.current = null;
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const poll = async (): Promise<void> => {
      if (pollingInFlightRef.current || !activeRef.current) {
        return;
      }

      pollingInFlightRef.current = true;

      try {
        const result = await getNem12BatchStatus(responseBatchId);

        if (!activeRef.current) {
          return;
        }

        if (!result.isSuccess || !result.data) {
          return;
        }

        const manifest = result.data.data.manifest;
        const status = String(
          result.data.batch?.status ?? manifest?.status ?? "Processing",
        );
        const normalizedStatus = normalizeStatus(status);
        const previousStatus = previousStatusRef.current;

        if (previousStatus === normalizedStatus) {
          return;
        }

        previousStatusRef.current = normalizedStatus;
        if (!isProcessingStatus(status)) {
          stopPolling();
          shownBatchStatusRef.batchId = responseBatchId;
          shownBatchStatusRef.status = normalizedStatus;
          const { text, type } = getStatusMessage(status, () =>
            navigate(ROUTES.dataTools.nem12),
          );
          showToastDynamic(text, type, undefined, {
            autoClose: POLL_INTERVAL_MS,
          });
        }
      } finally {
        if (pollingInFlightRef.current && activeRef.current) {
          pollingInFlightRef.current = false;
        }
      }
    };

    void poll();
    timerRef.current = window.setInterval(() => void poll(), POLL_INTERVAL_MS);

    return (): void => {
      stopPolling();
    };
  }, [navigate, responseBatchId, isOnNem12ViewerPage]);
};
